import { useCallback, useRef } from "react";
import { useChatStore } from "../stores/chatStore";
import { getSessionId } from "./useSession";
import type { AIState, ChatMessage, SiteSpecContent } from "@imonsite/shared";

interface UseSSEOptions {
  url: string;
  onComplete?: () => void;
  maxRetries?: number;
  retryDelayMs?: number;
}

export function useSSE(options: UseSSEOptions) {
  const { url, onComplete, maxRetries = 3, retryDelayMs = 1000 } = options;
  const abortControllerRef = useRef<AbortController | null>(null);
  const retryCountRef = useRef(0);

  const {
    addMessage,
    mergeSpec,
    setAIState,
    setStreaming,
    appendStreamingText,
    clearStreamingText,
    setError,
    setConnected,
  } = useChatStore();

  const sendMessage = useCallback(
    async (message: string, siteId: string) => {
      // Abort any existing request
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setStreaming(true);
      clearStreamingText();
      setError(null);

      // Add user message optimistically
      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        siteId,
        role: "user",
        content: message,
        createdAt: new Date().toISOString(),
      };
      addMessage(userMessage);

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Session-ID": getSessionId(),
          },
          body: JSON.stringify({ message }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("No response body");
        }

        setConnected(true);
        retryCountRef.current = 0;

        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE format: event: X\ndata: Y\n\n
          const events = buffer.split("\n\n");
          buffer = events.pop() || "";

          for (const eventStr of events) {
            if (!eventStr.trim()) continue;

            const lines = eventStr.split("\n");
            let eventType = "";
            let eventData = "";

            for (const line of lines) {
              if (line.startsWith("event: ")) {
                eventType = line.slice(7);
              } else if (line.startsWith("data: ")) {
                eventData = line.slice(6);
              }
            }

            if (!eventType) continue;

            switch (eventType) {
              case "thinking":
                // AI is thinking, show indicator
                break;

              case "token": {
                // Streaming token - just update indicator, don't show raw JSON
                // We'll show the final parsed text when we get the message event
                break;
              }

              case "message": {
                // Complete message with parsed AI output
                const msgData = JSON.parse(eventData) as {
                  message: ChatMessage;
                  output: { type: string; text?: string; spec?: object };
                };
                // Extract text from any response type (question, spec, or patch)
                const displayText = msgData.output.text || 
                  (msgData.output.type === "spec" ? "Got it! Updating your site details..." : "");
                // Only add message if there's text to display
                if (displayText) {
                  addMessage({
                    ...msgData.message,
                    content: displayText,
                  });
                }
                break;
              }

              case "state": {
                // AI state transition
                const stateData = JSON.parse(eventData) as { state: AIState };
                setAIState(stateData.state);
                break;
              }

              case "spec": {
                // Spec update
                const specData = JSON.parse(eventData) as {
                  spec: Partial<SiteSpecContent>;
                  version: number;
                };
                mergeSpec(specData.spec);
                break;
              }

              case "done":
                // Stream complete
                clearStreamingText();
                setStreaming(false);
                onComplete?.();
                break;

              case "error": {
                const errorData = JSON.parse(eventData) as {
                  message: string;
                  code: string;
                };
                setError(errorData.message);
                setStreaming(false);
                break;
              }
            }
          }
        }
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          return; // User cancelled
        }

        setConnected(false);

        // Retry logic
        if (retryCountRef.current < maxRetries) {
          retryCountRef.current++;
          setTimeout(() => {
            sendMessage(message, siteId);
          }, retryDelayMs * retryCountRef.current);
          return;
        }

        setError(
          error instanceof Error
            ? error.message
            : "Connection failed. Please try again."
        );
        setStreaming(false);
      }
    },
    [
      url,
      onComplete,
      maxRetries,
      retryDelayMs,
      addMessage,
      mergeSpec,
      setAIState,
      setStreaming,
      appendStreamingText,
      clearStreamingText,
      setError,
      setConnected,
    ]
  );

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    setStreaming(false);
  }, [setStreaming]);

  return { sendMessage, cancel };
}

